﻿import { CenterOnOffPilot, DonorShieldStatus } from '../enums/index';
import { IDashIntakeConfig } from './dash-intake-config';
import { ICenterAddress } from './center-address';
import { IUnosData } from './center-profile';
import { IVersionedFile } from './center-profile';
import { IDocumentsVersion } from './center-profile';
import { IInsuranceCarrierInfo } from './center-profile';
import { ISerologyTest } from './serology-test';
import { ICenterNotification } from './notifications';

export interface ICenter {
    id: number;
    isDeleted: boolean;
    isTestCenter: boolean;
    alias: string;
    name: string;
    userName: string;
    password: string;
    webUrl: string;
    nkrBillingContactNotes: string;
    labInstructions: string;
    isNkrParticipant: boolean;
    transplantStatus: boolean;
    transplantRestartDate: string,
    isAllowedLocalCryo: boolean;
    isRdnParticipant: boolean;
    isInListingService: boolean;
    isPrivate: boolean;

    // liaison info

    nkrLiaisonId: number;
    nkrLiaisonName: string;
    nkrLiaisonEmail: string,
    nkrLiaisonPhone: string,

    // executive sponsor info

    executiveSponsorId: number;
    executiveSponsorName: string;

    hasAgreedToFees: boolean;
    isAllIn: boolean;
    isGoldStar: boolean;
    isActiveStrongBoxCenter?: boolean
    annualFee: number;
    dcnAnnualFee: number;
    monthlyFee: number;
    monthlyFeeNotes: string;
    dashFee: number;
    dashNotes: string,
    donorShieldFee: number,
    isAcceptingReferrals: boolean;
    nddReferrals: number;
    isAdpPartner: boolean;
    isAdpParticipant: boolean;
    isFamilyVoucherParticipant: boolean;
    isDcnParticipant: boolean;
    isDppParticipant: boolean;
    isStreamlinedBilling: boolean;
    isFollowUpParticipant: boolean;
    isDashLiverParticipant: boolean;
    isInDonorShield: DonorShieldStatus;
    isDonorShieldDirectLiverCenter: boolean;
    isMicrositeParticipant: boolean;
    isAccParticipant: CenterOnOffPilot;
    hasCenterBilling: boolean;
    betaActivationStatus: CenterOnOffPilot;
    dashDomain: string;
    excludeDataCompliance: boolean;
    nkrFee: string;
    fNcs3: number;
    cprScore: number;
    chainEndTarget: number,
    clc: number;
    fClc: number;
    clCu: number;
    hasAgreementFile: boolean;
    w9FormPath: string;
    isMedicalBoard: boolean;
    declineRatio: number;
    reduceEmails: boolean;
    isUtilizing3gKidneyForLife: boolean;
    bizDevNextSteps: string;
    bizDevNextStepsTargetDate: string;
    notes: string;
    category: string;
    useMicrositeResponsibilityConfirmation: boolean;
    dashIntakeConfig: IDashIntakeConfig;
    centerAddresses: ICenterAddress[];
    centerUnosData: IUnosData[];
    insuranceCarriers: IInsuranceCarrierInfo[];
    versionedFiles: IVersionedFile[];
    documentsVersions: IDocumentsVersion[];

    // serology data

    pcrTestHours: number,
    pcrTestHoursDescription: string;
    serologyMaxDaysBeforeSurgery: number,
    serologyTurnaround: number,
    isSerologyParticipant: number,
    requiresQfg: number,
    serologyShipToId: number,
    serologyTests: ISerologyTest[],

    // profile/center data

    opoId: number;
    earliestOrganPickupTime: string;
    latestOrganDropoffTime: string;
    timezone: string;
    avgCostLDEvalWorkup: number;
    doesOpoPackorPerf: number;
    opoCostPackAndPerf: number;
    invoicePoNumber: string;
    achRoutingNumber: string;
    achAccountNumber: string;
    streamlinedHospitalBilling: boolean;
    physicianDonorNephrectomyCharges: number;
    provideSwapChecklists: boolean;

    // SRTR data

    hasGraftSurvivalFile: boolean;
    dcAdultGraftSurvival1: number;
    dcAdultGraftSurvival3: number;
    dcPediatricGraftSurvival1: number;
    dcPediatricGraftSurvival3: number;
    adultGraftSurvival1: number;
    adultGraftSurvival3: number;
    pediatricGraftSurvival1: number;
    pediatricGraftSurvival3: number;
    dcAdultPatientSurvival1: number;
    dcAdultPatientSurvival3: number;
    dcPediatricPatientSurvival1: number;
    dcPediatricPatientSurvival3: number;
    adultPatientSurvival1: number;
    adultPatientSurvival3: number;
    pediatricPatientSurvival1: number;
    pediatricPatientSurvival3: number;
    sRTRSourceDate: string;

    // lab data

    hasFrozenScreening: boolean;
    hasFrozenExploratory: boolean;
    hasRecipientShield: boolean;
    xmVirtualOption: number;
    labLavender85: number;
    labLavender40: number;
    labLavender70: number;
    labLavender100: number;
    labPink60: number;
    labPink100: number;
    labRed70: number;
    labRed100: number;
    labGreen100: number;
    labYellow85: number;
    transLavender85: number;
    transLavender40: number;
    transLavender70: number;
    transLavender100: number;
    transPink60: number;
    transPink100: number;
    transRed70: number;
    transRed100: number;
    transGreen100: number;
    transYellow85: number;

    // services data

    performsIncompatibleTransplants: number;
    performsDesensitization: number;
    performsNonSteroidProtocol: number;
    maxPairWorkups?: number;
    minNDDAge?: number;
    providesDonorInsurance: number;
    donorWorkupWait?: number;
    recipWorkupWait?: number;
    maxNDDAge?: number;
    chainEndTargets: number,
    // hasAbPatients: number,
    // hasAbPatientsLastUpdated: string,
    cmsFormPath: string;

    // anatomy prefs data

    anatomyPrefArteriesLeft?: number;
    anatomyPrefArteriesRight?: number;
    anatomyPrefVeinsLeft?: number;
    anatomyPrefVeinsRight?: number;
    
    notifications: ICenterNotification[];
    fvTarget: number;
    ncsDeficit: number;
    ncsRank: number;
    hasAbPatients: boolean;
    // doesA2ChainEndOptimization: boolean;
    canUtilizeA2toBPatients: boolean;

    isExpertSystemParticipant: boolean,

    leadCoordinatorId: number
}
